import { useCallback, useEffect, useRef, useState } from 'react';

import type { FilesAttachmentSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/files';

import { errorMessage } from '../../../../../redux/messages/thunks';
import { useAppDispatch } from '../../../../../redux/redux_hooks';
import { useLatest } from '../../../../hooks/use_latest';
import {
  ACCEPTED_ATTACHMENT_TYPES_LABEL,
  isAcceptedAttachmentFile,
  MAX_COMMENT_ATTACHMENTS,
} from '../utils/attachment_files';

export type PendingAttachmentFile = {
  localId: string;
  file: File;
  name: string;
  previewUrl: string;
};

/** What one send took from the form: it either completes or is released. */
export type PendingAttachmentsClaim = {
  files: PendingAttachmentFile[];
  removedIds: string[];
};

const isEmptyClaim = (taken: PendingAttachmentsClaim) =>
  taken.files.length === 0 && taken.removedIds.length === 0;

type UsePendingAttachmentsParams = {
  /** The attachments of the comment being edited. */
  savedAttachments?: FilesAttachmentSchemaWithIdNullable[];
};

/**
 * The files picked in one comment form, and the saved ones removed from it.
 * Nothing reaches the backend until the form is sent.
 *
 * Send and auto-save can fire for the same form concurrently, so each send
 * claims a snapshot of the files: complete() drops only that snapshot and
 * release() returns it on failure.
 */
export function usePendingAttachments({
  savedAttachments = [],
}: UsePendingAttachmentsParams = {}) {
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<PendingAttachmentFile[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [claimedIds, setClaimedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const savedAttachmentsRef = useLatest(savedAttachments);
  // The refs are the source of truth, written before state, so claim() and the
  // cap never read a render that is behind the last mutation.
  const filesRef = useRef<PendingAttachmentFile[]>([]);
  const removedIdsRef = useRef<string[]>([]);
  // Kept outside state so two sends in the same tick never claim the same file.
  const claimedRef = useRef(new Set<string>());
  const claimsRef = useRef(new Set<PendingAttachmentsClaim>());

  const visibleSavedAttachments = savedAttachments.filter(
    (attachment) => !removedIds.includes(attachment.id),
  );

  const commitFiles = useCallback((next: PendingAttachmentFile[]) => {
    filesRef.current = next;
    setFiles(next);
  }, []);

  const commitRemovedIds = useCallback((next: string[]) => {
    removedIdsRef.current = next;
    setRemovedIds(next);
  }, []);

  const addFiles = useCallback(
    (picked: Iterable<File>) => {
      const all = Array.from(picked);
      const accepted = all.filter(isAcceptedAttachmentFile);
      if (accepted.length < all.length) {
        dispatch(
          errorMessage({
            text: `Only ${ACCEPTED_ATTACHMENT_TYPES_LABEL} can be attached`,
          }),
        );
      }
      const savedCount = savedAttachmentsRef.current.filter(
        (attachment) => !removedIdsRef.current.includes(attachment.id),
      ).length;
      const room = Math.max(
        0,
        MAX_COMMENT_ATTACHMENTS - savedCount - filesRef.current.length,
      );
      if (accepted.length > room) {
        dispatch(
          errorMessage({
            text: `You can attach up to ${MAX_COMMENT_ATTACHMENTS} images`,
          }),
        );
      }
      const added = accepted.slice(0, room).map((file) => ({
        localId: crypto.randomUUID(),
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
      }));
      if (added.length > 0) {
        commitFiles([...filesRef.current, ...added]);
      }
    },
    [commitFiles],
  );

  const removeFile = useCallback(
    (localId: string) => {
      const target = filesRef.current.find((file) => file.localId === localId);
      if (!target) {
        return;
      }
      URL.revokeObjectURL(target.previewUrl);
      commitFiles(filesRef.current.filter((file) => file !== target));
    },
    [commitFiles],
  );

  const removeSaved = useCallback(
    (attachmentId: string) => {
      if (removedIdsRef.current.includes(attachmentId)) {
        return;
      }
      commitRemovedIds([...removedIdsRef.current, attachmentId]);
    },
    [commitRemovedIds],
  );

  const claim = useCallback((): PendingAttachmentsClaim => {
    const claimed = claimedRef.current;
    const savedIds = new Set(
      savedAttachmentsRef.current.map((attachment) => attachment.id),
    );
    const taken: PendingAttachmentsClaim = {
      files: filesRef.current.filter((file) => !claimed.has(file.localId)),
      removedIds: removedIdsRef.current.filter(
        (attachmentId) =>
          !claimed.has(attachmentId) && savedIds.has(attachmentId),
      ),
    };
    for (const file of taken.files) {
      claimed.add(file.localId);
    }
    for (const attachmentId of taken.removedIds) {
      claimed.add(attachmentId);
    }
    claimsRef.current.add(taken);
    if (!isEmptyClaim(taken)) {
      setClaimedIds(new Set(claimed));
    }
    return taken;
  }, []);

  const unclaim = useCallback((taken: PendingAttachmentsClaim) => {
    if (!claimsRef.current.delete(taken)) {
      return false;
    }
    for (const file of taken.files) {
      claimedRef.current.delete(file.localId);
    }
    for (const attachmentId of taken.removedIds) {
      claimedRef.current.delete(attachmentId);
    }
    if (!isEmptyClaim(taken)) {
      setClaimedIds(new Set(claimedRef.current));
    }
    return true;
  }, []);

  const release = useCallback(
    (taken: PendingAttachmentsClaim) => {
      unclaim(taken);
    },
    [unclaim],
  );

  const complete = useCallback(
    (taken: PendingAttachmentsClaim) => {
      if (!unclaim(taken) || isEmptyClaim(taken)) {
        return;
      }
      const sentIds = new Set(taken.files.map((file) => file.localId));
      for (const file of taken.files) {
        URL.revokeObjectURL(file.previewUrl);
      }
      commitFiles(
        filesRef.current.filter((file) => !sentIds.has(file.localId)),
      );
      commitRemovedIds(
        removedIdsRef.current.filter(
          (attachmentId) => !taken.removedIds.includes(attachmentId),
        ),
      );
    },
    [unclaim, commitFiles, commitRemovedIds],
  );

  const isSending = useCallback(() => claimsRef.current.size > 0, []);

  const reset = useCallback(() => {
    for (const file of filesRef.current) {
      URL.revokeObjectURL(file.previewUrl);
    }
    claimsRef.current.clear();
    claimedRef.current.clear();
    setClaimedIds(new Set());
    commitFiles([]);
    commitRemovedIds([]);
  }, [commitFiles, commitRemovedIds]);

  useEffect(() => {
    return () => {
      for (const file of filesRef.current) {
        URL.revokeObjectURL(file.previewUrl);
      }
    };
  }, []);

  return {
    files,
    savedAttachments: visibleSavedAttachments,
    claimedIds,
    hasAttachments: visibleSavedAttachments.length + files.length > 0,
    isUploading: files.some((file) => claimedIds.has(file.localId)),
    addFiles,
    removeFile,
    removeSaved,
    claim,
    release,
    complete,
    isSending,
    reset,
  };
}

export type PendingAttachments = ReturnType<typeof usePendingAttachments>;
